import { Effect, Option } from 'effect';

import { getBrowserResourceEndpoint } from '../config/api';
import { getGcsPublicUrl } from '../config/image';

import { RuntimeClientConfig } from '../runtime/RuntimeClientConfig';
import type { ImageFilterT } from './ImageFilter';

const PATH_ABSTRACT_PREFIX = /^\/media\/cache\/resolve\/\{filter\}\//;
const FILTER_PLACEHOLDER = /\{filter\}/g;

// Strip the templated prefix from a `pathAbstract` to recover the relative
// segment, e.g.
//
//   `/media/cache/resolve/{filter}/be/01/foo.webp` → Some(`be/01/foo.webp`)
//
// Returns `None` when the input doesn't carry the expected prefix or has no
// relative tail. Pure (no Config), so it can be unit-tested without a runtime.
export function parsePathAbstractRel(pathAbstract: string): Option.Option<string> {
  const stripped = pathAbstract.replace(PATH_ABSTRACT_PREFIX, '');
  // `String.replace` returns the input unchanged if no match; detect that.
  if (stripped === pathAbstract) return Option.none();
  return stripped ? Option.some(stripped) : Option.none();
}

// Resolve a backend image template into a concrete image URL for one filter.
// Prefers the CDN URL when `NEXT_PUBLIC_GCS_PUBLIC_URL` is set; otherwise
// falls back to the backend `resolve` URL under `API_ENTRYPOINT`.
//
//   CDN     `${gcsPublicUrl}/cache/{filter}/{rel}`
//   Backend `${apiEndpoint}/media/cache/resolve/{filter}/{rel}`
//
// If `template` doesn't match the expected templated shape, the CDN branch is
// skipped and the backend branch performs a raw `{filter}` substitution.
// There is no default filter: the rendering slot owns that choice.
export const resolveImageUrlEffect = (template: string, filter: ImageFilterT) =>
  Effect.gen(function* () {
    const cdnEnv = yield* getGcsPublicUrl;
    const apiEndpoint = yield* getBrowserResourceEndpoint;

    // Build the CDN URL only when both the CDN env *and* the parseable
    // template are present; either missing piece collapses to None.
    const cdnUrl = Option.flatMap(cdnEnv, (cdnPrefix) =>
      Option.map(parsePathAbstractRel(template), (rel) => `${cdnPrefix}/cache/${filter}/${rel}`)
    );

    return Option.getOrElse(
      cdnUrl,
      () => `${apiEndpoint}${template.replace(FILTER_PLACEHOLDER, filter)}`
    );
  });

// Sync wrapper for render-time call sites. Reads config through
// `RuntimeClientConfig`, which runs on both client and server and is built
// once from the shell's public env at module load.
export function resolveImageUrl(template: string, filter: ImageFilterT): string {
  return RuntimeClientConfig.runSync(resolveImageUrlEffect(template, filter));
}
